package one.devsbox.www.ui.resources

import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import compose.assets.Svg
import compose.assets.svg.UploadOutline
import kotlinx.coroutines.launch
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.files.ALL
import one.devsbox.shared.files.ResourceType
import one.devsbox.shared.files.RotationType
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.ScreenContentAction
import one.devsbox.www.provider.NavigationCallbackProvider
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.tools.root
import one.devsbox.www.www_portal.generated.resources.*
import tk.mallumo.compose.navigation.navTo_ResourceUI
import tk.mallumo.compose.navigation.navTo_UploaderUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState

@VM
class ListingResourcesVM : NavigationViewModel() {
    private lateinit var ctx: ListingResourcesCtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    var state by mutableStateOf<DataState<SharedIO.Resources>>(DataState.Loading())
        private set

    var loadingMore by mutableStateOf(false)
        private set

    val search = TextFieldState()
    var selectedOrientation by mutableStateOf<Int>(RotationType.ALL)
    var selectedMediaType by mutableStateOf<Int>(ResourceType.ALL)

    interface Action {
        fun setOrientation(orientation: Int)
        fun setMediaType(type: Int)
        fun loadMore()
        fun reload()
        fun itemSelected(media: SharedIO.MediaSimple)
        fun upload()
        fun back()
    }

    val action = object : Action {
        override fun setOrientation(orientation: Int) {
            selectedOrientation = orientation
            loadResources()
        }

        override fun setMediaType(type: Int) {
            selectedMediaType = type
            loadResources()
        }

        override fun loadMore() {
            val currentState = state
            if (currentState is DataState.Result && !loadingMore && currentState.nnvl.items.size < currentState.nnvl.count) {
                loadResources(page = currentState.nnvl.page + 1)
            }
        }

        override fun reload() {
            loadResources()
        }

        override fun back() {
            ctx.nav.up()
        }

        override fun itemSelected(media: SharedIO.MediaSimple) {
            ctx.pickerTask?.also { taskMedia ->
                ctx.navCallback.publisher.publish(
                    key = ctx.args.pickerModeKey,
                    task = taskMedia.copy(
                        response = NavigationCallbackProvider.Task.Media.MediaResponse(
                            id = media.id,
                            name = media.name,
                            aspectRatio = media.aspectRatio,
                            type = media.type,
                            duration = media.duration,
                            width = media.width,
                            height = media.height,
                        )
                    )
                )
                ctx.nav.up()
            } ?: run {
                ctx.nav.navTo_ResourceUI(ArgsId(media.id))
            }
        }

        override fun upload() {
            ctx.nav.root().navTo_UploaderUI()
        }
    }

    fun setup(ctx: ListingResourcesCtx) {
        this.ctx = ctx
        if (state is DataState.Loading) {
            ctx.pickerTask?.also {
                selectedMediaType = it.request.type
                selectedOrientation = it.request.orientation
            }

        }
        loadResources()
    }

    private fun loadResources(page: Int = 0) {
        if (page == 0) state = DataState.Loading()
        else loadingMore = true

        scope.launch {
            runCatching {
                Repository.ads.media.filter(selectedMediaType, selectedOrientation, page)
            }.onSuccess { response ->
                if (page == 0) {
                    state =
                        DataState.Result(response.copy(items = response.items.sortedByDescending { it.create }))
                } else {
                    val currentResources = (state as? DataState.Result)?.nnvl
                    if (currentResources != null) {
                        state = DataState.Result(
                            currentResources.copy(
                                page = response.page,
                                items = currentResources.items + response.items.sortedByDescending { it.create }
                            )
                        )
                    }
                }
                loadingMore = false
                setupActions()
            }.onFailure {
                if (page == 0) state = DataState.Error(it.message ?: "Unknown error")
                loadingMore = false
                contentActions = emptyList()
            }
        }
    }

    private fun setupActions() {
        contentActions = buildList {
            if (ctx.me.permission.media.write && !ctx.me.info.adminMode) {
                add(
                    ScreenContentAction(
                        text = Res.string.ui_resources_listing_btn_upload,
                        color = ctx.colorPrimaryContainer,
                        icon = Svg.UploadOutline
                    ) {
                        action.upload()
                    }
                )
            }
        }
    }

    override fun onRelease() {}
}



